import React, {
   CSSProperties,
   forwardRef,
   ReactElement, ReactNode,
   Ref,
   useCallback,
   useEffect, useMemo,
   useState
} from 'react';
import { DirtyStateChangeEvent, Form, FormApi, ValidationErrors } from '../form';
import { ModalButtonBar } from '../../controls/modalbuttonbar';
import { Drawer } from '../../controls/drawer';

/**
 * {@docCategory FormDrawer}
 */
export interface FormDrawerProps<T> {
   /**
    * Specifies if the Drawer is visible.
    * @defaultvalue false
    */
   visible?: boolean;

   /**
    * Sets the title of the Drawer. If the title is not defined and closable is false then
    * the Drawer header will not be shown.
    */
   title?: JSX.Element | string;

   /**
    * The underlying form name.  This is used to namespace form field ids for screen readers and
    * also to associate any submit button with the form.
    */
   name: string;

   /**
    * The placement of the Drawer.
    * @defaultvalue 'right'
    */
   position?: 'left' | 'right' | 'top' | 'bottom';

   /**
    * Placement is top or bottom, height of the Drawer dialog
    */
   height?: number;

   /**
    * Width of the Drawer dialog
    */
   width?: number;

   /**
    * Called when the form data needs to be saved.
    * @param values - The values
    * @param form - The form api
    */
   onSubmit?: (values: T, form: FormApi<T>) => void;

   /**
    * The initial values to display in the fields.
    */
   initialValues?: T;

   /**
    * Form level validation function
    * @param values - The values
    */
   validate?: (values: T) => ValidationErrors | Promise<ValidationErrors> | undefined;

   /**
    * Callback that is called when the user presses the Cancel button,
    * closes the Drawer using the close button, the ESC key, or by clicking on the mask.
    */
   onCancel?: () => void;

   /**
    * Text to override the default text for the OK button.
    * @defaultvalue 'OK'
    */
   okText?: ReactNode;

   /**
    * Text to override the default text for the Cancel button.
    * @defaultvalue 'Cancel'
    */
   cancelText?: ReactNode;

   /**
    * Whether or not to hide the cancel button
    */
   hideCancelButton?: boolean;

   /**
    * Extra content on the left side of the button bar
    */
   buttonsLeftContent?: ReactNode;

   /**
    * If this is set, it enables analytics pageviews for the drawer.  Set this to the simple name
    * of the Drawer using all lowercase with dashes to separate words.  For example:
    * approve-employee-request.
    */
   analyticsName?: string;

   /**
    * A callback that notifies the listener whether or not the form is currently saving
    * @param saving - true if is saving, false otherwise
    */
   onSavingStateChange?: (saving: boolean) => void;

   /**
    * A callback that notifies the listener whether or not the form has unsaved changes
    * @param event - The dirty state change event object
    */
   onDirtyStateChange?: <T,>(event: DirtyStateChangeEvent<T>) => void;

   /**
    * Expand drawer to take up the full screen if position is top or bottom.
    * This should only be used if the drawer is almost full screen anyway for cosmetic purposes.
    */
   fullScreen?: boolean;

   /**
    * The class name of the container of the Drawer dialog.
    */
   className?: string;

   /**
    * Style of wrapper element.
    */
   style?: CSSProperties;

   /**
    * Content to be displayed in the Drawer's content area.
    */
   children?: ReactNode;
}

const bodyStyle = {
   padding: 0
};

export const FormDrawer = forwardRef(function FormDrawer<T, >(props: FormDrawerProps<T>, ref: Ref<FormApi<T>>) {

   const { name, cancelText, okText, onCancel, onSavingStateChange, visible, buttonsLeftContent, hideCancelButton } = props;

   const [saving, setSaving] = useState<boolean>(false);

   useEffect(()=>{
      if (visible) {
         setSaving(false);
      }
   }, [visible]);

   const onSavingStateChangeHandler = useCallback((saving: boolean) => {
      setSaving(saving);
      if (onSavingStateChange) {
         onSavingStateChange(saving);
      }
   },[onSavingStateChange]);

   const footer = useMemo(() => {
      return (<ModalButtonBar
         okType={'submit'}
         disableOk={saving}
         disableCancel={saving}
         cancelText={cancelText}
         okText={okText}
         onCancel={onCancel}
         formName={name}
         leftContent={buttonsLeftContent}
         hideCancel={hideCancelButton}
      />);
   }, [name, cancelText, okText, onCancel, saving, hideCancelButton, buttonsLeftContent]);

   return (
      <Drawer
         visible={props.visible}
         title={props.title}
         analyticsName={props.analyticsName}
         position={props.position}
         width={props.width}
         height={props.height}
         className={props.className}
         style={props.style}
         fullScreen={props.fullScreen}
         onClose={props.onCancel}
         footer={footer}
         closable={!saving}
         bodyStyle={bodyStyle}
      >
         <Form<T>
            name={props.name}
            onSubmit={props.onSubmit}
            initialValues={props.initialValues}
            validate={props.validate}
            onDirtyStateChange={props.onDirtyStateChange}
            onSavingStateChange={onSavingStateChangeHandler}
            ref={ref}
         >
            {props.children}
         </Form>
      </Drawer>
   );
}) as <T>(props: FormDrawerProps<T> & React.RefAttributes<FormApi<T>>) => ReactElement;
